/**
 * Single source of truth for the admin access-control vocabulary (step 0.3).
 *
 * Every guard, the nav config and the region context import roles, regions and
 * capabilities from here. Values are typed against the unions below, so a typo
 * (e.g. 'editer') fails `tsc` rather than silently denying access at runtime.
 *
 * These names are CASE-SENSITIVE and match the canonical values set on real
 * Cognito users (`custom:role` / `custom:region`).
 */

// --- Roles ------------------------------------------------------------------
export const ROLES = [
  'super_admin',
  'editor',
  'viewer',
  'support',
  'fraud',
  'compliance',
] as const;
export type Role = (typeof ROLES)[number];

// --- Regions ----------------------------------------------------------------
// GLOBAL means the user can access both UK and US.
export const REGIONS = ['UK', 'US', 'GLOBAL'] as const;
export type Region = (typeof REGIONS)[number];
/** A concrete, data-bearing region (GLOBAL is a scope, not a place). */
export type AccessibleRegion = 'UK' | 'US';

// --- Capabilities -----------------------------------------------------------
// A capability is `<resource>:<action>`, e.g. 'payouts:write'. Surfaces declare
// the capability they need; roles hold a set of grants. Grants may use
// wildcards: '*' (everything) or '*:read' (read on any resource).
//
// ACTIONS — `read` / `write` are the resource-CRUD actions (list/show vs
// create/edit/delete). Beyond those, capabilities may be **action-specific** for
// accountable operational DECISIONS that warrant a finer grant than "can write
// this resource": `approve` (authorise a cashout: approve/reject — D-payout-
// boundary) and `resolve` (resolve a stuck cashback: confirm/reject —
// D-cashback-reject). This is the pattern for the higher-trust decisions to
// come (fraud holds, manual attribution resolution, …): give the decision its
// OWN capability string so it can be granted to exactly the right role, instead
// of being implied by a coarse `:write`. The `*` wildcard (super_admin) still
// covers them; `*:read` (viewer) deliberately does not.
// `cancel_renewal` and `comp` are the Subscriptions action capabilities
// (D-subscriptions): cancelling a member's renewal is something the member could
// do themselves, so it is granted to `editor`; comping a free Plus period confers
// real value, so it stays super_admin-only (the Phase 4 trust rule). `issue` is
// the Promo-codes value-committing capability (D-promo-codes): creating a code or
// raising its value is super_admin-only, while editor keeps the value-reducing
// `promo_codes:write` (deactivate, descriptive edits). Direction of value sorts
// the permission, not the resource.
export type Action =
  | 'read'
  | 'write'
  | 'approve'
  | 'resolve'
  | 'cancel_renewal'
  | 'comp'
  | 'issue'
  // `void` is the Referrals guarded action (D-referrals): killing a fraudulent
  // referral stops a reward, the value-reducing direction, so it is editor.
  | 'void'
  // The Manual attribution actions (D-manual-attribution), sorted by direction of
  // value: `credit` LINKS an orphan transaction to a member (value-committing, the
  // transaction starts counting toward that member once confirmed), so it stays
  // super_admin-only via '*'; `reject` (the transaction will never count for
  // anyone) and `flag` (mark needs-review, no value moves) are value-stopping /
  // value-neutral, so they are editor. In Phase 5 the fraud reviewer inherits
  // `attribution:credit` (manual crediting is fraud-adjacent); fraud stays empty
  // until that surface is designed.
  | 'credit'
  | 'reject'
  | 'flag'
  // The Fraud-queue actions (D-fraud-queue), sorted by REVERSIBILITY (not direction
  // of value). The fraud reviewer holds the reversible-or-recoverable ones:
  // `suspend` (temporary hold), `escalate` (routing only, no status change),
  // `reinstate` (lift a suspension, which was temporary by construction) and `ban`
  // (a recorded decision the reviewer owns). `unban` reverses a ban: the most
  // terminal write, so it is super_admin-only (a second higher hand), withheld from
  // the fraud role and covered only by '*'. No fraud action mutates value: a ban is
  // a status, never a balance write.
  | 'suspend'
  | 'ban'
  | 'escalate'
  | 'reinstate'
  // The Suspension-flow appeal action (D-suspension-flow). `dismiss_appeal` resolves
  // a pending appeal WITHOUT reinstating (the original decision stands): it lifts
  // nothing and reverses nothing, so by the reversibility rule it sits at FRAUD
  // alongside suspend / reinstate. Overturning an appeal reuses the existing
  // reinstate (suspension, fraud) or unban (ban, super_admin), so it needs no new
  // capability and inherits that asymmetry for free.
  | 'dismiss_appeal'
  // The Ban-evasion action (D-ban-evasion). `dismiss_link` marks a suspected
  // same-person link as investigated-not-evasion: a value-neutral recorded decision
  // that clears nothing and reverses nothing (it only suppresses the link until a
  // stronger match re-raises it), so by the reversibility rule it sits at FRAUD.
  // Confirmed evasion REUSES the existing users:ban against the linked account, so
  // it needs no new capability.
  | 'dismiss_link'
  // The Chargebacks actions (D-chargebacks), sorted ASYMMETRICALLY BY OUTCOME.
  // `review` covers the value-NEUTRAL dispute work (record, move to under_review,
  // add the evidence note, resolve WON, which keeps the cashback): a FRAUD action.
  // `resolve_loss` resolves a dispute LOST, which writes a reversal ledger entry and
  // claws back value: the value-moving financial reversal, SUPER_ADMIN-only (the
  // same tier as cashbacks:resolve), covered for super_admin by '*' and withheld
  // from fraud. A fraud reviewer can resolve a dispute won but NOT lost.
  | 'review'
  | 'resolve_loss'
  // The Ticket-inbox action (D-ticket-inbox, the Phase 6 lead). `assign` sets a
  // ticket's agent. The other ticket actions reuse existing actions: `resolve` (close
  // the ticket) and `escalate` (route to a higher hand). All are recorded decisions
  // through the chokepoint, audited for free, granted to the support agent.
  | 'assign'
  // The Manual balance adjustment action (D-balance-adjustment), the Phase 5
  // capstone. `adjust` moves value directly, chosen by an admin with no underlying
  // event, as a signed LEDGER ENTRY the balance derives from (never a direct balance
  // write). It is ENTIRELY value-committing (no neutral part to delegate), so it is
  // the highest-trust write: SUPER_ADMIN-only, covered solely by '*' and granted to
  // no other role. There is no maker-checker (the team is three trusted super_admins;
  // the deferred upgrade, enforced approver-not-proposer, needs actor-aware transition
  // logic the chokepoint does not have today). The fat-finger guard is a per-
  // adjustment cap confirm-gate in the surface, not a separate capability.
  | 'adjust'
  | 'unban'
  // The DSAR actions (D-dsar, the Phase 7 lead). A DSAR is a data-subject access
  // request: a member asking for everything the platform holds on them, fulfilled by
  // the compliance officer within a STATUTORY deadline. `process` moves a request along
  // its handling (received -> in_progress) and `fulfil` produces the assembled data
  // package and marks it fulfilled. Both are value-NEUTRAL: they record the handling of
  // a regulated request, they move no money and touch no operational state. `reject`
  // (refuse an invalid or unverified request) is REUSED from the union above. All three
  // are compliance writes, covered for super_admin by '*', and granted to no operational
  // or financial role.
  | 'process'
  | 'fulfil'
  // The Consent-records action (D-consent-records, Phase 7 surface 2). Consent is an
  // APPEND-ONLY history of a member's grants and revocations; the surface is READ-ONLY
  // oversight (the consumer app writes grants and app-driven revocations, the engine-written
  // seam) EXCEPT this ONE narrow write. `record_withdrawal` appends a REVOKE record sourced
  // `compliance_logged` for a member who exercised their right to withdraw via a non-app
  // channel (phone / email) and which compliance must honour and record. It is value-NEUTRAL
  // (it records an exercised right, moves no value), a compliance write covered for
  // super_admin by '*' and granted to no operational or financial role.
  | 'record_withdrawal'
  // The Sanctions-screening actions (D-sanctions, Phase 7 surface 3). Members are screened by
  // an engine against the sanctions lists (OFAC, UK HMT, PEP); the engine produces MATCHES the
  // admin reviews, it never computes them here. `clear` resolves a match as a false positive (a
  // name collision, the common case); `confirm` resolves it as a true match (a legal
  // determination that the platform must not transact with this person). Both are value-NEUTRAL
  // recorded determinations through the chokepoint with a mandatory reason; confirming records a
  // sanctions status, it does NOT move value and does NOT flip fraudStatus, and the downstream
  // enforcement (login block, payout hold) is a cross-repo seam. Both sit at compliance (the
  // named sanctions authority) and super_admin via '*'.
  | 'clear'
  | 'confirm'
  // The Erasure actions (D-erasure, Phase 7 surface 6, the last and sharpest). Right-to-be-forgotten
  // is the most TERMINAL action in the build: an executed erasure cannot be undone (an unban can be
  // re-banned, an adjustment reversed; erasure cannot). So the trust is SPLIT as a maker-checker built
  // in from the start. `request` logs an erasure request, `cancel` cancels one during the grace window,
  // and the REUSED `reject` refuses one (the banned-evader / retention-conflict adversarial case): all
  // three are value-NEUTRAL recorded decisions held by COMPLIANCE (the data-protection authority that
  // judges validity). `execute` is the irreversible act (the owned-row tombstone fires, the backend
  // cascade follows): SUPER_ADMIN-only via '*', the second higher hand on the unrecoverable step (the
  // users:unban / balances:adjust asymmetry, taken further). Compliance proposes, super_admin executes.
  | 'request'
  | 'cancel'
  | 'execute'
  // The Rate-rule actions (D-rate-rules, Phase 8 surface 2). Rule AUTHORING is editor draft
  // work; ACTIVATING a rule to live is the consequential going-live act, so it gets its OWN
  // capability above plain authoring, separately audited and reservable to a tighter hand later
  // without rework. Activation is REVERSIBLE via `pause` (the safety net), so it is NOT raised a
  // tier (unlike the terminal erasure:execute / balances:adjust): all three sit at editor and
  // super_admin via '*'. `author` create/edit a rule in draft, `activate` takes a draft or paused
  // rule live, `pause` reversibly disables an active rule (the same hand can pull back its own
  // going-live). All value-affecting but reversible recorded decisions through the chokepoint.
  | 'author'
  | 'activate'
  | 'pause'
  // The Page-schema publish actions (D-page-schema-editor-8b, Phase 8 surface 3b).
  // Authoring a draft is editor work (`pages:author`, 8a); PUBLISHING the draft to
  // live is the consequential going-live act with its OWN capability (`publish`),
  // separately audited and reservable to a tighter hand later without rework, exactly
  // mirroring rate_rules:activate. `rollback` reverts to a prior published version
  // (the reversibility net, the analogue of rate_rules:pause): its own capability so
  // the same hand that publishes can pull back. Publishing is reversible via rollback
  // (a rollback is itself a publish of a prior snapshot), so BOTH stay at editor and
  // super_admin via '*', NOT a tier jump (unlike the terminal erasure:execute /
  // balances:adjust). Both route through the chokepoint, validated first in the
  // surface, audited.
  | 'publish'
  | 'rollback'
  // The Settings config action (D-settings-s1, Phase 9 S1). `configure` is the
  // reason-forced config write for the HIGHER-CONSEQUENCE settings (cashback
  // defaults, the value affecting what members earn), DISTINCT from the low-stakes
  // `settings:write` (site name, currency display, automatic audit only). A
  // distinct string so it can be tightened to super_admin later with a one-line
  // grant move (the rate_rules:activate precedent), without rework. This is the one
  // new action literal S1 needs; every other S1 capability reuses `write`. The
  // Plus marketing flag reuses `write` (marketing_flags:write) but is granted only
  // to super_admin (via '*'), so a marketing flip stays above editor.
  | 'configure';
export type Capability = `${string}:${Action}`;
export type Grant = '*' | `*:${Action}` | Capability;

/**
 * What each role can do.
 *
 * Fully wired now: `super_admin` (everything) and `viewer` (read everywhere,
 * write nowhere). `editor` is the DESCRIPTIVE-record editing role: it holds
 * `<resource>:read` + `<resource>:write` for each descriptive surface as it
 * lands — `stores:*` (D-stores-edit), `users:*` (D-users-edit), `charities:*`
 * (D-charity-softdelete) and `categories:*` (D-categories-light) — all
 * descriptive records, like a store. It is
 * deliberately NOT granted the action-specific FINANCIAL capabilities
 * (`cashouts:approve`, `cashbacks:resolve`), which stay super_admin-only
 * (D-action-capabilities) — authorising money movement is higher-trust than
 * editing a descriptive record. `fraud` is now populated (D-fraud-queue, the Phase
 * 5 lead): a SPECIALIST ORTHOGONAL role (reads + a tight recorded-decision set,
 * see its grant list below). `support` is now populated (D-support-role, the Phase 6
 * lead): another SPECIALIST ORTHOGONAL role with reads plus a BOUNDED job set (the
 * ticket actions, a CAPPED positive-only support credit, the reused suspend, and USE of
 * the shared canned-response library via `canned_responses:read`), holding NONE of
 * editor's descriptive writes, NONE of super_admin's unbounded financial authorisations,
 * and NOT `canned_responses:write` (managing the shared library stays super_admin-only).
 * `compliance` is now populated (D-compliance-role, the Phase 7 lead): the INVERSE of the
 * operational specialists, a BROAD-READ-NARROW-WRITE OVERSIGHT role. It reads wide across the
 * member context a DSAR must produce and holds only the legally-mandated DSAR writes
 * (dsar:process / dsar:fulfil / the reused reject, the consent-records reads plus the one narrow
 * consent:record_withdrawal, and the sanctions review actions sanctions:clear / sanctions:confirm),
 * and NONE of the operational or financial writes (no ban / suspend / credit / adjust /
 * payout-approve / descriptive-edit). Audit-log read is deferred to the audit-query surface, so it
 * is not granted here yet.
 *
 * Note each `:write` travels with its `:read`: you cannot meaningfully edit a
 * record you cannot see (the list, then the detail).
 */
export const PERMISSIONS: Record<Role, readonly Grant[]> = {
  super_admin: ['*'],
  viewer: ['*:read'],
  editor: [
    'stores:read',
    'stores:write',
    'users:read',
    'users:write',
    'charities:read',
    'charities:write',
    'categories:read',
    'categories:write',
    // Subscriptions (D-subscriptions): editor reads the surface and may cancel a
    // member's renewal (a write the member could do themselves). Comping a free
    // Plus period (subscriptions:comp) is withheld — that confers real value and
    // stays super_admin-only via the '*' grant.
    'subscriptions:read',
    'subscriptions:cancel_renewal',
    // Promo codes (D-promo-codes): editor reads, and may DEACTIVATE and edit
    // descriptive fields (the value-reducing direction) via promo_codes:write.
    // Creating a code or raising its value (promo_codes:issue) confers value and
    // stays super_admin-only via the '*' grant.
    'promo_codes:read',
    'promo_codes:write',
    // Referrals (D-referrals): editor reads the explorer and may VOID a
    // fraudulent referral (value-stopping, the safe direction). There is no
    // admin reward-paying action at all (paying is the engine's).
    'referrals:read',
    'referrals:void',
    // Placements (D-placements): INTERNAL MERCHANDISING with no money attached, so
    // the whole CRUD sits at editor via placements:write (descriptive, like
    // categories). No value-split this version: if placements ever become sold
    // inventory, add a price field and move create / weight-raise / window-extend
    // to super_admin by the direction-of-value test (the promo-codes split).
    'placements:read',
    'placements:write',
    // Manual attribution (D-manual-attribution): editor reads the queue and may
    // REJECT an unattributable transaction (value-stopping) and FLAG one for review
    // (no value). Crediting an orphan to a member (attribution:credit) commits value
    // and stays super_admin-only via the '*' grant. The fraud role inherits credit
    // in Phase 5 and is left empty until then.
    'attribution:read',
    'attribution:reject',
    'attribution:flag',
    // Rate rules (D-rate-rules, Phase 8 surface 2): editor authors rules in draft
    // (rate_rules:author + the implied rate_rules:read), takes a draft / paused rule live
    // (rate_rules:activate, the going-live act, separately audited) and pauses an active rule
    // (rate_rules:pause, the reversibility net). Activation is value-affecting but reversible via
    // pause, so it stays at editor with its OWN capability rather than a tier jump; super_admin
    // covers all via '*'. The going-live capability can be reserved to a tighter hand later
    // without rework because it is already a distinct string.
    'rate_rules:read',
    'rate_rules:author',
    'rate_rules:activate',
    'rate_rules:pause',
    // Page schemas (D-page-schema-editor-8a, Phase 8 surface 3a): the editor AUTHORS
    // schema-driven page DRAFTS (pages:author + the implied pages:read), reusing the
    // existing `author` action (the rate_rules:author precedent). This is the authoring
    // CORE only: there is NO publish path in 8a. The going-live capability `pages:publish`
    // (and rollback) is sub-surface 8b; it is deliberately NOT added here, so no
    // unreachable grant ships. Authoring is reversible (an unpublished draft affects
    // nothing live), so it sits at editor; super_admin covers it via '*'.
    'pages:read',
    'pages:author',
    // Page-schema publish (D-page-schema-editor-8b): the editor PUBLISHES a draft to
    // live (pages:publish, the going-live act, separately audited) and ROLLS BACK to a
    // prior published version (pages:rollback, the reversibility net). Two DISTINCT
    // capabilities mirroring rate_rules:activate / rate_rules:pause, so the going-live
    // act can be reserved to a tighter hand later without rework while the net stays at
    // editor. Both are reversible (a rollback undoes a publish), so both sit at editor;
    // super_admin covers all via '*'.
    'pages:publish',
    'pages:rollback',
    // Conventional rich-text CMS (D-cms-pages-core, Phase 8): the editor AUTHORS rich-text
    // marketing pages (cms:author + the implied cms:read) and PUBLISHES a draft to live
    // (cms:publish, the going-live act, separately audited), mirroring the pages:author /
    // pages:publish split. This is a DISTINCT resource from the page-schema pages, so it
    // gets its OWN capability rather than reusing pages:*. The actions author / publish
    // already exist in the union; only the cms-resource grants are new. Both reversible
    // (unpublishing returns to draft), so both sit at editor; super_admin covers all via '*'.
    'cms:read',
    'cms:author',
    'cms:publish',
    // Settings S1 (D-settings-s1, Phase 9): the editor reads the settings surface
    // (settings:read, which also gates the API docs reference), edits the low-stakes
    // config (settings:write: site name, currency display, automatic audit only),
    // changes the cashback defaults with a FORCED REASON (settings:configure, the
    // distinct string for later tightening), and toggles feature flags with a FORCED
    // REASON (feature_flags:write). It deliberately does NOT hold marketing_flags:write:
    // flipping the Plus marketing flag platform-wide is super_admin-only via '*' (the
    // locked Q1 decision), above the editor tier. All four are new PERMISSIONS grant
    // entries; only settings:configure needed a new action literal.
    'settings:read',
    'settings:write',
    'settings:configure',
    'feature_flags:write',
    // Settings S3 (D-settings-s3-email-templates, Phase 9): the editor edits the fixed
    // set of transactional email templates (email_templates:write: subject + body per
    // market, automatic audit, no forced reason, the cms:author content-authoring
    // posture). A new grant reusing the existing write action, so NO new action literal.
    // super_admin covers it via '*'.
    'email_templates:write',
  ],
  // Support agent (D-support-role, the Phase 6 lead): a SPECIALIST ORTHOGONAL role, a
  // capability GRID not a rung on a ladder. Reads the member context it works (users,
  // transactions, the ticket inbox, a member's cashouts) plus a TIGHT BOUNDED job set:
  // the ticket actions (assign / resolve / escalate), a CAPPED POSITIVE-ONLY support
  // credit (balances:credit, NOT the unbounded super_admin balances:adjust), the
  // REUSED suspend (users:suspend, the same recorded decision the fraud role writes),
  // and escalate (route a member to a higher hand). It holds NONE of editor's
  // descriptive `*:write` grants (NO users:write, so NO change-tier and NO delete), and
  // NONE of super_admin's financial authorisations (cashouts:approve / cashbacks:resolve
  // / chargebacks:resolve_loss / balances:adjust). super_admin covers all of it via '*'.
  support: [
    'users:read',
    'transactions:read',
    'tickets:read',
    'cashoutRequests:read',
    'tickets:assign',
    'tickets:resolve',
    'tickets:escalate',
    // Canned responses (D-canned-responses, the Phase 6 surface-2 library): the support
    // agent USES the shared library (browse, render with substitution, copy out, run a
    // macro's text plus its OWN actions) via the neutral `canned_responses:read`. It does
    // NOT hold `canned_responses:write`: MANAGING the library (create / edit / retire)
    // edits copy that reaches every member at once, so it stays super_admin-only via '*'
    // (the direction-of-trust rule, like promo issue-vs-write).
    'canned_responses:read',
    // The bounded support credit: a capped, positive-only ledger entry. DISTINCT from
    // the unbounded super_admin balances:adjust.
    'balances:credit',
    // The reused recorded decisions: suspend a member (the same users:suspend the fraud
    // role writes) and escalate one to a higher hand.
    'users:suspend',
    'users:escalate',
  ],
  // Fraud reviewer (D-fraud-queue): a SPECIALIST ORTHOGONAL role, not a rung on a
  // ladder. It holds the reads the fraud queue needs plus a TIGHT recorded-decision
  // action set, and NONE of editor's descriptive `*:write` grants and NONE of
  // super_admin's financial authorisations (cashouts:approve / cashbacks:resolve).
  // The trust split is by REVERSIBILITY: the reviewer can suspend (temporary),
  // escalate (routing only), reinstate a suspension (it was temporary) and ban (a
  // decision it owns), but it CANNOT reverse a ban: `users:unban` is the most
  // terminal write and stays super_admin-only via '*'. super_admin covers every
  // fraud capability through '*'. This is the capability GRID, not a strict ladder.
  fraud: [
    'fraud_queue:read',
    'users:read',
    'transactions:read',
    'cashoutRequests:read',
    'users:suspend',
    'users:ban',
    'users:escalate',
    'users:reinstate',
    // Suspension flow (D-suspension-flow): dismissing a pending appeal stops it
    // without lifting the hold, so it stays at fraud. Overturning an appeal reuses
    // users:reinstate (suspension) or users:unban (ban, super_admin only), so no
    // extra grant is needed and the ban-overturn asymmetry holds for free.
    'users:dismiss_appeal',
    // Ban evasion (D-ban-evasion): dismissing a suspected link is value-neutral and
    // reversible (a stronger match re-raises it), so it stays at fraud. Confirmed
    // evasion reuses users:ban (already granted) against the linked account.
    'users:dismiss_link',
    // Chargebacks (D-chargebacks): the reviewer works the dispute (record, review,
    // evidence note, resolve WON) which moves no value, so chargebacks:review is at
    // fraud. Resolving LOST writes a reversal and claws back value, so
    // chargebacks:resolve_loss is super_admin-only (via '*') and NOT granted here.
    'chargebacks:review',
  ],
  // Compliance officer (D-compliance-role, the Phase 7 lead): the INVERSE of the
  // operational specialists. Where support and fraud are narrow-read-tight-write, the
  // compliance role is BROAD-READ-NARROW-WRITE oversight: it reads wide across the
  // member context it must produce in a DSAR (users, transactions, cashouts, tickets),
  // and holds only the legally-mandated DSAR writes (dsar:process / dsar:fulfil, and the
  // REUSED reject). It holds NONE of the operational or financial writes: no ban /
  // suspend / escalate, no balances:credit / adjust, no cashouts:approve, no
  // cashbacks:resolve, and none of editor's descriptive *:write. The DSAR writes are
  // value-neutral (they record the handling of a regulated request, they move no value).
  // super_admin covers every compliance capability through '*'. Audit-log read is
  // DEFERRED to the audit-query surface (where the audit gate expansion to compliance is
  // wired and recorded as a considered decision); it is NOT granted here.
  compliance: [
    'users:read',
    'transactions:read',
    'cashoutRequests:read',
    'tickets:read',
    'dsar:read',
    'dsar:process',
    'dsar:fulfil',
    'dsar:reject',
    // Consent records (D-consent-records, Phase 7 surface 2): the compliance officer READS a
    // member's consent history (consent:read) and may record a withdrawal received off-app
    // (consent:record_withdrawal, the one narrow write). Both value-neutral oversight; the
    // consumer app remains the writer of grants and app-driven revocations.
    'consent:read',
    'consent:record_withdrawal',
    // Sanctions screening (D-sanctions, Phase 7 surface 3): the compliance officer READS the
    // engine-produced match queue (sanctions:read) and reviews each match, either CLEARING a
    // false positive (sanctions:clear) or CONFIRMING a true match (sanctions:confirm). Both sit
    // at compliance, the named sanctions authority where the expertise to judge a hit lives;
    // both move no value (confirming records a legal determination, the downstream enforces).
    'sanctions:read',
    'sanctions:clear',
    'sanctions:confirm',
    // Erasure (D-erasure, Phase 7 surface 6): the compliance officer READS the erasure worklist
    // (erasure:read), LOGS a request (erasure:request), CANCELS one during the grace window
    // (erasure:cancel), and REJECTS the adversarial cases (erasure:reject, reused). It does NOT hold
    // erasure:execute: the irreversible execute is super_admin-only via '*' (the maker-checker, the
    // second higher hand on the most terminal action). Compliance proposes, super_admin executes.
    'erasure:read',
    'erasure:request',
    'erasure:cancel',
    'erasure:reject',
    // Tax reporting (D-tax-reporting, Phase 7 surface 4): the compliance officer READS the
    // derived 1099-K report of US members over the IRS reporting threshold (tax:read). This is a
    // PURE READ surface: there is no write, no filing lifecycle, and no capability for one. A real
    // 1099-K generation and filing is the cross-repo seam; a filed-status write is deferred until
    // that exists.
    'tax:read',
    // US compliance settings (D-settings-s4-us-compliance, Phase 9 S4, the last Settings surface):
    // the compliance officer EDITS the regulatory config (compliance_settings:write): the 1099-K
    // reporting thresholds the tax report reads, the per-state privacy toggles, and the privacy /
    // terms references into the CMS legal pages. Compliance OWNS regulatory config (it already holds
    // the dsar / sanctions / consent / erasure writes), so this is coherent, NOT the first surface it
    // can read but not administer. super_admin covers it via '*'. NEVER editor. It REUSES the write
    // action (no new action literal; S1's configure stays the sole Settings action literal). Every
    // change forces a reason (JustifiedAction), and the surface itself is role-gated compliance +
    // super_admin (the tax-report precedent).
    'compliance_settings:write',
  ],
};

const grantMatches = (grant: Grant, capability: Capability): boolean => {
  if (grant === '*') return true;
  if (grant === capability) return true;
  if (grant.startsWith('*:')) {
    const action = grant.slice(2);
    return capability.endsWith(`:${action}`);
  }
  return false;
};

/** Can this role perform this capability? */
export function can(role: Role, capability: Capability): boolean {
  return PERMISSIONS[role].some((grant) => grantMatches(grant, capability));
}

/** The concrete regions a user with the given region scope can see. */
export function accessibleRegions(region: Region): AccessibleRegion[] {
  switch (region) {
    case 'GLOBAL':
      return ['UK', 'US'];
    case 'UK':
      return ['UK'];
    case 'US':
      return ['US'];
  }
}

// --- Access decision (shared by route guards and menu gating) ---------------
export interface AccessContext {
  readonly role: Role | null;
  readonly accessibleRegions: readonly AccessibleRegion[];
}

export interface AccessRequirement {
  /** Capability the role must have (super_admin always passes). */
  readonly capability?: Capability;
  /** Explicit allow-list of roles (super_admin always passes). */
  readonly roles?: readonly Role[];
  /** Region the user must be able to access (applies to everyone). */
  readonly region?: AccessibleRegion;
}

/**
 * The single access decision used by both the route guard and the menu filter.
 * Pure and fully unit-tested.
 *
 * `super_admin` bypasses role/capability checks ("sees everything"), but the
 * region scope applies to every role, including super_admin.
 */
export function hasAccess(
  ctx: AccessContext,
  requirement: AccessRequirement,
): boolean {
  if (!ctx.role) return false;

  if (ctx.role !== 'super_admin') {
    if (requirement.capability && !can(ctx.role, requirement.capability)) {
      return false;
    }
    if (requirement.roles && !requirement.roles.includes(ctx.role)) {
      return false;
    }
  }

  if (
    requirement.region &&
    !ctx.accessibleRegions.includes(requirement.region)
  ) {
    return false;
  }

  return true;
}

// --- Safe parsing of raw token values ---------------------------------------
/** Narrow an unknown value (e.g. from the token) to a Role, or null. */
export function toRole(value: unknown): Role | null {
  return typeof value === 'string' &&
    (ROLES as readonly string[]).includes(value)
    ? (value as Role)
    : null;
}

/**
 * Narrow an unknown value (e.g. from the token) to a canonical Region, or null.
 *
 * The value is NORMALISED (trimmed + upper-cased) before matching, so a Cognito
 * `custom:region` that arrives as `global`, `Global` or `" GLOBAL "` still
 * resolves to `GLOBAL` (and likewise `uk`/`us`). Without this, a GLOBAL user
 * whose attribute isn't exactly upper-case falls through to the most-restrictive
 * fallback and loses multi-region access (e.g. the region switcher disappears).
 */
export function toRegion(value: unknown): Region | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  return (REGIONS as readonly string[]).includes(normalized)
    ? (normalized as Region)
    : null;
}
